import {useQuery} from "@tanstack/react-query";
import {useMemo} from "react";

import {formatCompanyVehicleOptionLabel} from "../lib/format-company-vehicle-option-label";
import {readPatrolStartSnapshot} from "../lib/patrol-start-snapshot";
import {companyVehiclesService} from "../services";
import type {VehicleConditionCheckRow} from "../types";

export type PatrolEndStartContext = {
  company_vehicle_id: number;
  start_odometer_miles: number | null;
  vehicle_label: string;
  /**
   * Filled in by the dialog: max(start check, vehicle `current_mileage`) for end-form default + min.
   * The hook leaves this `null`; the dialog merges vehicle detail.
   */
  end_mileage_floor_miles: number | null;
};

function labelFromCheckRow(row: VehicleConditionCheckRow): string {
  if (row.company_vehicle) {
    return formatCompanyVehicleOptionLabel(row.company_vehicle);
  }
  return `Vehicle #${row.company_vehicle_id}`;
}

/**
 * For patrol **end** checks: resolve the start check (API list by `shift_id`, else sessionStorage).
 */
export function usePatrolEndStartContext(
  shiftId: number,
  mode: "start" | "end",
  open: boolean,
) {
  const canList = companyVehiclesService.getCanListConditionChecks();

  const query = useQuery({
    queryKey: ["vehicleconditionchecks", "by-shift", shiftId] as const,
    queryFn: () => companyVehiclesService.getConditionChecksForShift(shiftId),
    enabled: open && mode === "end" && canList,
  });

  const startFromApi = useMemo(
    () =>
      query.data?.find(
        (c) => c.phase === "start" || c.check_type === "start",
      ) ?? null,
    [query.data],
  );

  const snapshot = useMemo(() => {
    if (mode !== "end" || !open) return null;
    return readPatrolStartSnapshot(shiftId);
  }, [mode, open, shiftId]);

  const startContext: PatrolEndStartContext | null = useMemo(() => {
    if (mode !== "end") return null;
    if (startFromApi) {
      return {
        company_vehicle_id: startFromApi.company_vehicle_id,
        start_odometer_miles: startFromApi.mileage ?? startFromApi.odometer_miles ?? null,
        vehicle_label: labelFromCheckRow(startFromApi),
        end_mileage_floor_miles: null,
      };
    }
    if (snapshot) {
      return {
        company_vehicle_id: snapshot.company_vehicle_id,
        start_odometer_miles: snapshot.odometer_miles,
        vehicle_label: snapshot.vehicle_label,
        end_mileage_floor_miles: null,
      };
    }
    return null;
  }, [mode, startFromApi, snapshot]);

  const isLoadingStart = Boolean(open && mode === "end" && canList && query.isPending);
  const isError = open && mode === "end" && canList && query.isError;
  const usedSnapshot = Boolean(!startFromApi && snapshot);

  return {
    startContext,
    isLoadingStart,
    isError,
    usedSnapshot,
    canListStartChecks: canList,
  };
}
